import * as React from 'react'
import { useState, useEffect, useMemo, useCallback } from 'react'

import uniqBy from 'lodash/uniqBy'

import { useComposerUploader } from '~publish/legacy/uploads/hooks/useComposerUploader'

import AppActionCreators from '../action-creators/AppActionCreators'
import { NotificationScopes } from '../AppConstants'
import Composer from './Composer'
import NotificationContainer from './NotificationContainer'
import ProductRolloutTooltip from './ProductRolloutTooltip'
import type Draft from '../entities/Draft/Draft'
import AppStore from '../stores/AppStore'
import ComposerStore from '../stores/ComposerStore'
import type { ComposerProfile } from '../stores/types'
import type { Notification } from '../values/Notification'

import styles from './css/ComposerSection.module.css'

const getComposerState = (): {
  enabledDrafts: Draft[]
  draftsSharedData: ReturnType<typeof ComposerStore.getDraftsSharedData>
  omniDraft?: Draft
  forceEditorFocus: boolean
} => ({
  enabledDrafts: ComposerStore.getEnabledDrafts(),
  draftsSharedData: ComposerStore.getDraftsSharedData(),
  omniDraft: ComposerStore.getDraft('omni'),
  forceEditorFocus: ComposerStore.getMeta().forceEditorFocus,
})

const ComposerComponent = ({
  draft,
  index,
  forceEditorFocus,
  profiles,
  isOmniboxEnabled,
  visibleNotifications,
  composerRootRef,
}: {
  draft: Draft
  index?: number
  forceEditorFocus: boolean
  profiles: ComposerProfile[]
  isOmniboxEnabled: boolean
  visibleNotifications: Notification[]
  composerRootRef: React.RefObject<HTMLDivElement>
}): JSX.Element => {
  const canUserPostToMultipleNetworks =
    uniqBy(profiles, (p) => p.service.name).length > 1
  const showRolloutTooltip =
    AppStore.getOptions().canSelectProfiles &&
    canUserPostToMultipleNetworks &&
    (isOmniboxEnabled || index === ComposerStore.getEnabledDrafts().length - 1)

  const uploader = useComposerUploader({ draft })

  const children = React.useMemo(
    () =>
      showRolloutTooltip ? (
        <ProductRolloutTooltip
          visibleNotifications={visibleNotifications}
          isOmniboxEnabled={isOmniboxEnabled}
        />
      ) : null,
    [showRolloutTooltip, isOmniboxEnabled, visibleNotifications],
  )

  // When focus should be forced, figure out *which* editor instance to force-focus
  const forceEditorInstanceFocus =
    forceEditorFocus &&
    (isOmniboxEnabled || AppStore.getAppState().expandedComposerId === draft.id)

  return (
    <Composer
      draft={draft}
      key={draft.id}
      forceEditorFocus={forceEditorInstanceFocus}
      expandedComposerId={AppStore.getExpandedComposerId()}
      uploader={uploader}
      composerRootRef={composerRootRef}
    >
      {children}
    </Composer>
  )
}

interface ComposerSectionProps {
  profiles?: ComposerProfile[]
  visibleNotifications: Notification[]
  isOmniboxEnabled?: boolean
  composerRootRef: React.RefObject<HTMLDivElement>
}

const ComposerSection = ({
  profiles = [],
  visibleNotifications,
  isOmniboxEnabled = false,
  composerRootRef,
}: ComposerSectionProps): JSX.Element => {
  const [composerState, setComposerState] = useState(getComposerState())

  useEffect(() => {
    const onStoreChange = (): void => {
      setComposerState(getComposerState())
    }

    ComposerStore.addChangeListener(onStoreChange)

    return () => {
      ComposerStore.removeChangeListener(onStoreChange)
    }
  }, [])

  const onTwitterMaxProfileNotificationClose = useCallback(() => {
    AppActionCreators.rememberTwitterMaxProfileNotificationClosedOnce()
  }, [])

  const { omniDraft, forceEditorFocus } = composerState
  const enabledDrafts = ComposerStore.getEnabledDrafts()
  const hasEnabledDrafts = enabledDrafts.length > 0 || isOmniboxEnabled
  const { composersHaveBeenExpanded } = AppStore.getAppState()

  const twitterMaxProfileNotificationClassNames = useMemo(
    () => ({
      container: styles.twitterMaxProfileNotificationContainer,
      notification: `${styles.twitterMaxProfileNotification} bi-notification`,
      notificationCloseButton: styles.twitterMaxProfileNotificationCloseButton,
    }),
    [],
  )

  return (
    <section className={[styles.composerSection, 'post-box'].join(' ')}>
      {!hasEnabledDrafts && AppStore.hasProfiles() && (
        <div className={styles.emptyState}>
          {composersHaveBeenExpanded
            ? 'Your work has been saved. Select a Channel to create a post.'
            : 'Select a Channel to create a post.'}
        </div>
      )}

      <NotificationContainer
        visibleNotifications={visibleNotifications}
        scope={NotificationScopes.TWITTER_MAX_ONE_PROFILE_SELECTED}
        classNames={twitterMaxProfileNotificationClassNames}
        onClose={onTwitterMaxProfileNotificationClose}
        showCloseIcon
      />

      {isOmniboxEnabled && (
        <ComposerComponent
          forceEditorFocus={forceEditorFocus}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temp assumption that omniDraft is always defined when isOmniboxEnabled is true
          draft={omniDraft!}
          profiles={profiles}
          isOmniboxEnabled={isOmniboxEnabled}
          visibleNotifications={visibleNotifications}
          composerRootRef={composerRootRef}
        />
      )}

      {!isOmniboxEnabled &&
        enabledDrafts.map((draft, index) => (
          <ComposerComponent
            key={draft.id}
            forceEditorFocus={forceEditorFocus}
            draft={draft}
            index={index}
            profiles={profiles}
            isOmniboxEnabled={isOmniboxEnabled}
            visibleNotifications={visibleNotifications}
            composerRootRef={composerRootRef}
          />
        ))}
    </section>
  )
}

export default ComposerSection
